import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Sparkle, ShieldCheck, ShoppingCart } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ACCENTS = [
  'from-pink-500 via-orange-400 to-yellow-400',
  'from-green-400 via-emerald-500 to-teal-500',
  'from-blue-500 via-indigo-400 to-purple-500',
];

const LOCALIZED_FALLBACK_PRODUCTS = {
  en: [
    {
      id: 'diet_mediterranean',
      title: '1-Week Mediterranean Diet Plan',
      description:
        'A heart-healthy 7-day meal plan focusing on whole grains, healthy fats, and lean proteins.',
      price_eur: 9.99,
      badge: 'Best Seller',
      features: ['7 daily meal plans with recipes', 'Complete grocery list', 'Meal prep guide'],
    },
    {
      id: 'diet_plantbased',
      title: '1-Week Plant-Based Diet Plan',
      description:
        'Discover vibrant, plant-forward meals designed to nourish your body and keep your energy steady all day.',
      price_eur: 9.99,
      badge: '100% Vegan',
      features: ['7 daily meal plans with recipes', 'Complete grocery list', '100% plant-based'],
    },
  ],
  es: [
    {
      id: 'diet_mediterranean',
      title: 'Plan de Dieta Mediterránea de 1 Semana',
      description:
        'Plan de 7 días saludable para el corazón con cereales integrales, grasas saludables y proteína magra.',
      price_eur: 9.99,
      badge: 'Más Vendido',
      features: ['7 menús diarios con recetas', 'Lista de la compra completa', 'Guía de meal prep'],
    },
    {
      id: 'diet_plantbased',
      title: 'Plan Plant-Based de 1 Semana',
      description:
        'Descubre comidas vegetales vibrantes diseñadas para nutrir tu cuerpo y mantener tu energía todo el día.',
      price_eur: 9.99,
      badge: '100% Vegano',
      features: ['7 menús diarios con recetas', 'Lista de la compra completa', '100% vegetal'],
    },
  ],
  it: [
    {
      id: 'diet_mediterranean',
      title: 'Piano Dieta Mediterranea di 1 Settimana',
      description:
        'Un piano di 7 giorni salutare per il cuore con cereali integrali, grassi sani e proteine magre.',
      price_eur: 9.99,
      badge: 'Più Venduto',
      features: ['7 menu giornalieri con ricette', 'Lista della spesa completa', 'Guida meal prep'],
    },
    {
      id: 'diet_plantbased',
      title: 'Piano Plant-Based di 1 Settimana',
      description:
        'Scopri pasti vegetali bilanciati studiati per nutrire il tuo corpo e mantenere alta l’energia.',
      price_eur: 9.99,
      badge: '100% Vegano',
      features: ['7 menu giornalieri con ricette', 'Lista della spesa completa', '100% vegetale'],
    },
  ],
};

const Shop = () => {
  const { t, i18n } = useTranslation();
  const langCode = (i18n.language || 'en').split('-')[0];
  const fallbackForLang =
    LOCALIZED_FALLBACK_PRODUCTS[langCode] || LOCALIZED_FALLBACK_PRODUCTS.en;

  const [remoteProducts, setRemoteProducts] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    const lang = (i18n.language || 'en').split('-')[0];
    fetch(`${API_URL}/api/diets?lang=${encodeURIComponent(lang)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setRemoteProducts(data.slice(0, 4));
        }
      })
      .catch(() => {});
  }, [i18n.language]);

  const products = remoteProducts || fallbackForLang;

  const purchaseSteps = [
    {
      icon: BookOpen,
      title: t('shop.step1Title', 'Choose your ebook'),
      detail: t(
        'shop.step1Detail',
        'Browse the collection curated for specific goals such as Mediterranean balance, low-carb, or plant-based nutrition.'
      ),
    },
    {
      icon: ShoppingCart,
      title: t('shop.step2Title', 'Secure checkout'),
      detail: t(
        'shop.step2Detail',
        'Pay safely with cards or digital wallets via Stripe Checkout — no account required.'
      ),
    },
    {
      icon: ShieldCheck,
      title: t('shop.step3Title', 'Instant PDF download'),
      detail: t(
        'shop.step3Detail',
        'Download your PDF immediately after checkout and receive a backup link by email.'
      ),
    },
  ];

  const handleCheckout = async (dietId) => {
    setLoadingId(dietId);
    try {
      const res = await fetch(
        `${API_URL}/api/payments/create-diet-checkout?diet_id=${encodeURIComponent(dietId)}`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setLoadingId(null);
      }
    } catch {
      setLoadingId(null);
    }
  };

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-purple-50 via-rose-50 to-amber-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-flex items-center px-4 py-1 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 text-white text-sm font-semibold uppercase tracking-wide shadow-lg">
            {t('shop.badge', 'eBook Shop')}
          </span>
          <h2 className="mt-6 text-4xl sm:text-5xl font-extrabold text-gray-900">
            {t('shop.titlePrefix', 'Nourish your body with')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 via-orange-500 to-yellow-500">
              {t('shop.titleHighlight', 'expert diet eBooks')}
            </span>
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
            {t(
              'shop.subtitle',
              'Each guide is crafted by Paola Michelle to turn nutrition science into delicious, doable action plans. Pick the ebook that matches your goals and download immediately after checkout.'
            )}
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 grid gap-8 md:grid-cols-2">
            {products.map((product, idx) => {
              const accent = ACCENTS[idx % ACCENTS.length];
              const priceLabel = `€${Number(product.price_eur ?? 9.99).toFixed(2)}`;
              return (
                <article
                  key={product.id}
                  className="relative rounded-3xl bg-white shadow-xl overflow-hidden border border-white/60 hover:border-white transition-transform duration-300 hover:-translate-y-1"
                >
                  <div
                    className={`absolute -top-24 -right-24 w-52 h-52 bg-gradient-to-br ${accent} opacity-40 blur-3xl`}
                  />
                  <div className="relative p-8 h-full flex flex-col">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center text-xs font-bold uppercase tracking-wide text-white bg-gray-900/80 px-3 py-1 rounded-full shadow-lg">
                        {product.badge || 'eBook'}
                      </span>
                      <span className="text-3xl font-extrabold text-gray-900">{priceLabel}</span>
                    </div>
                    <h3 className="mt-6 text-2xl font-bold text-gray-900">{product.title}</h3>
                    <p className="mt-3 text-gray-600 flex-1">{product.description}</p>
                    <ul className="mt-6 space-y-2">
                      {(product.features || []).map((highlight, index) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <Sparkle className="w-4 h-4 text-pink-500 mt-0.5 mr-2 flex-shrink-0" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handleCheckout(product.id)}
                      disabled={loadingId === product.id}
                      className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-400 text-white font-semibold px-6 py-3 shadow-lg shadow-orange-200 hover:shadow-xl hover:scale-[1.02] transition disabled:opacity-50"
                    >
                      {loadingId === product.id
                        ? t('diets.buttons.redirecting', 'Redirecting…')
                        : `${t('shop.buyNow', 'Buy now')} (${priceLabel})`}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          <aside className="bg-white rounded-3xl shadow-xl p-8 border border-white/60 flex flex-col justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {t('shop.howItWorksTitle', 'How purchasing works')}
              </h3>
              <p className="mt-3 text-gray-600">
                {t(
                  'shop.howItWorksSubtitle',
                  'Follow this guided flow for a smooth checkout experience and start reading within minutes.'
                )}
              </p>
              <div className="mt-6 space-y-6">
                {purchaseSteps.map((step, index) => (
                  <div key={step.title} className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-orange-500 text-white flex items-center justify-center font-bold text-lg shadow-lg">
                      {index + 1}
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center gap-2">
                        <step.icon className="w-5 h-5 text-pink-500" />
                        <h4 className="text-lg font-semibold text-gray-900">{step.title}</h4>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">{step.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 rounded-2xl bg-gradient-to-r from-fuchsia-500 via-purple-500 to-indigo-500 text-white p-6 shadow-2xl">
              <h4 className="text-xl font-semibold">
                {t('shop.ctaTitle', 'Need a personalised plan?')}
              </h4>
              <p className="mt-2 text-sm text-purple-50">
                {t(
                  'shop.ctaBody',
                  'Book a 1-on-1 consultation with Paola and receive a nutrition plan tailored to your health history and lifestyle.'
                )}
              </p>
              <a
                href="/booking"
                className="mt-4 inline-flex items-center justify-center rounded-full bg-white/90 text-purple-700 font-semibold px-5 py-2 hover:bg-white"
              >
                {t('shop.ctaButton', 'Book a consultation')}
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Shop;
